import { useState } from "react";
import {
  Ai,
  Link,
  Document,
  CheckmarkFilled,
  ArrowRight,
} from "@carbon/icons-react";
import { useTranslation } from "react-i18next";
import { Button } from "..";
import { useUpdateCompanyProfile } from "../../hooks/useCompany";
import { useFileInputUploader } from "../../hooks/useFileInputUploader";
import { CompanyProfileRequest, FileType } from "../../types";
import { useNotification } from "../../context";

interface CompanySetupBannerProps {
  companyProfile: any;
  onClose?: () => void;
}

const CompanySetupBanner: React.FC<CompanySetupBannerProps> = ({
  companyProfile,
  onClose,
}) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const [website, setWebsite] = useState(companyProfile?.website || "");
  const [catalogFile, setCatalogFile] = useState<File | null>(null);

  const fileUploader = useFileInputUploader(
    (data) => {
      handleCompanyProfileUpdate(data.fileURL);
    },
    (error) => {
      openNotification(t("companyProfile.setup.banner.upload.error"));
    }
  );

  const updateCompanyProfile = useUpdateCompanyProfile(
    (data) => {
      openNotification(t("companyProfile.profile.updated.success"));
      onClose?.();
    },
    (error) => {
      openNotification(t("companyProfile.profile.updated.error"));
    }
  );

  const handleCompanyProfileUpdate = (catalogFileURL?: string) => {
    const updatedFiles = [...(companyProfile?.files || [])];

    if (catalogFileURL) {
      const filteredFiles = updatedFiles.filter(
        (file) => file.type !== FileType.PRODUCT_CATALOG
      );

      filteredFiles.push({
        id: "",
        fileURL: catalogFileURL,
        name: catalogFile?.name || "Product Catalog",
        description: "Material Catalog",
        type: FileType.PRODUCT_CATALOG,
      });

      updatedFiles.splice(0, updatedFiles.length, ...filteredFiles);
    }

    const updateData: CompanyProfileRequest = {
      companyId: companyProfile.id,
      name: companyProfile.name,
      website: website || companyProfile.website,
      taxId: companyProfile.taxId,
      commercialName: companyProfile.commercialName,
      linkedinUrl: companyProfile.linkedinUrl,
      annualRevenue: companyProfile.annualRevenue,
      taxDocuments: companyProfile.taxDocuments,
      addresses: companyProfile.addresses || [],
      contacts: companyProfile.contacts || [],
      certifications: companyProfile.certifications || [],
      mainClients: companyProfile.mainClients || [],
      files: updatedFiles,
      bankingInfos: companyProfile.bankingInfos || [],
    };

    updateCompanyProfile.mutate(updateData);
  };

  const handleSubmit = async () => {
    if (!website && !catalogFile) {
      openNotification(t("companyProfile.setup.banner.validation.required"));
      return;
    }

    if (catalogFile) {
      fileUploader.mutate(catalogFile);
    } else {
      handleCompanyProfileUpdate();
    }
  };

  const isLoading = updateCompanyProfile.isPending || fileUploader.isPending;
  const hasWebsite = companyProfile?.website;
  const hasProductCatalog = companyProfile?.files?.some(
    (file: any) => file.type === FileType.PRODUCT_CATALOG
  );

  if (hasWebsite && hasProductCatalog) {
    return null;
  }

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border border-blue-200/50 rounded-xl shadow-lg mb-6">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-indigo-500/5 to-purple-500/5"></div>
      <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-xl"></div>

      <div className="relative p-6">
        <div className="flex items-start space-x-4">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <Ai size={24} className="text-white" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                {t("companyProfile.setup.banner.title")}
              </h3>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {t("companyProfile.setup.banner.quick")}
              </span>
            </div>

            <p className="text-gray-600 text-sm mb-6 leading-relaxed">
              {t("companyProfile.setup.banner.description")}
            </p>

            {/* Setup Steps */}
            <div className="space-y-4 mb-6">
              {!hasWebsite && (
                <div className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-white/20 shadow-sm">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Link size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {t("companyProfile.setup.banner.website.label")}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {t("companyProfile.setup.banner.website.description")}
                      </p>
                    </div>
                  </div>
                  <input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder={t(
                      "companyProfile.setup.banner.website.placeholder"
                    )}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                </div>
              )}

              {!hasProductCatalog && (
                <div className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-white/20 shadow-sm">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Document size={16} className="text-indigo-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {t("companyProfile.setup.banner.catalog.label")}
                      </h4>
                      <p className="text-xs text-gray-500">
                        {t("companyProfile.setup.banner.catalog.description")}
                      </p>
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="file"
                      id="catalog-upload"
                      onChange={(e) =>
                        setCatalogFile(e.target.files?.[0] || null)
                      }
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-400 transition-colors cursor-pointer">
                      <div className="text-center">
                        <Document
                          size={24}
                          className="mx-auto text-gray-400 mb-1"
                        />
                        <p className="text-xs text-gray-600">
                          {catalogFile
                            ? catalogFile.name
                            : t("companyProfile.setup.banner.catalog.upload")}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          PDF, DOC, DOCX, XLS, XLSX
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Button */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <CheckmarkFilled size={16} className="text-green-500" />
                <span>{t("companyProfile.setup.banner.secure")}</span>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={isLoading || (!website && !catalogFile)}
                btnClasses="inline-flex items-center px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-medium rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t("companyProfile.setup.banner.updating")}
                  </>
                ) : (
                  <>
                    {t("companyProfile.setup.banner.update")}
                    <ArrowRight size={16} className="ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySetupBanner;
